import { COPY } from "../config/brand";
import {
  BUBBLE_WEAPON_CLOGS,
  ENEMIES,
  EXTRA_LIFE_CLOGS,
  MAX_LIVES,
  PLUNGER_WEAPON_CLOGS,
  STARTING_LIVES,
  type EnemyKind,
  type PowerUpKind,
  type WeaponKind,
} from "../config/game";

export interface RunState {
  score: number;
  clogs: number;
  combo: number;
  bestCombo: number;
  lives: number;
  shield: number;
  heldPowerUp: "triple" | null;
  tripleUntil: number;
  weapon: WeaponKind;
  milestone: string | null;
  result: "playing" | "victory" | "defeat";
}

export interface ClogResult {
  state: RunState;
  points: number;
  milestone: string | null;
  lifeAwarded: boolean;
  weaponAwarded: Exclude<WeaponKind, "poop"> | null;
}

export interface EscapeResult {
  state: RunState;
  shieldConsumed: boolean;
  lifeLost: boolean;
}

export interface RunOptions {
  startingLives?: number;
  startingWeapon?: WeaponKind;
  allowWeaponProgression?: boolean;
}

export function createRunState(options: RunOptions = {}): RunState {
  return {
    score: 0,
    clogs: 0,
    combo: 0,
    bestCombo: 0,
    lives: Math.min(
      MAX_LIVES,
      Math.max(1, Math.floor(options.startingLives ?? STARTING_LIVES)),
    ),
    shield: 0,
    heldPowerUp: null,
    tripleUntil: 0,
    weapon: options.startingWeapon ?? "poop",
    milestone: null,
    result: "playing",
  };
}

export function registerClog(
  current: RunState,
  kind: EnemyKind,
  options: Pick<RunOptions, "allowWeaponProgression"> = {},
): ClogResult {
  if (current.result !== "playing")
    return {
      state: current,
      points: 0,
      milestone: null,
      lifeAwarded: false,
      weaponAwarded: null,
    };

  const combo = current.combo + 1;
  const clogs = current.clogs + (kind === "boss" ? 0 : 1);
  const multiplier = 1 + Math.min(Math.floor((combo - 1) / 5), 4) * 0.25;
  const points = Math.round(ENEMIES[kind].score * multiplier);
  const milestone =
    clogs > 0 && clogs % 10 === 0
      ? (COPY.messages[(clogs / 10 - 1) % COPY.messages.length] ??
        COPY.messages[0])
      : null;
  const lifeAwarded =
    kind !== "boss" && clogs === EXTRA_LIFE_CLOGS && current.lives < MAX_LIVES;
  const weaponAwarded =
    kind === "boss" || options.allowWeaponProgression === false
      ? null
      : clogs === PLUNGER_WEAPON_CLOGS
        ? "plunger"
        : clogs === BUBBLE_WEAPON_CLOGS
          ? "bubble"
          : null;
  const result = kind === "boss" ? "victory" : current.result;
  const state: RunState = {
    ...current,
    lives: lifeAwarded ? current.lives + 1 : current.lives,
    score: current.score + points,
    clogs,
    combo,
    bestCombo: Math.max(current.bestCombo, combo),
    milestone,
    weapon: weaponAwarded ?? current.weapon,
    result,
  };
  return { state, points, milestone, lifeAwarded, weaponAwarded };
}

export function registerEscape(current: RunState): EscapeResult {
  if (current.result !== "playing")
    return { state: current, shieldConsumed: false, lifeLost: false };
  if (current.shield > 0) {
    return {
      state: {
        ...current,
        shield: current.shield - 1,
        combo: 0,
        milestone: null,
      },
      shieldConsumed: true,
      lifeLost: false,
    };
  }

  const lives = Math.max(0, current.lives - 1);
  return {
    state: {
      ...current,
      lives,
      combo: 0,
      milestone: null,
      result: lives === 0 ? "defeat" : "playing",
    },
    shieldConsumed: false,
    lifeLost: true,
  };
}

export function collectPowerUp(current: RunState, kind: PowerUpKind): RunState {
  if (current.result !== "playing") return current;
  return kind === "shield"
    ? { ...current, shield: Math.min(1, current.shield + 1) }
    : { ...current, heldPowerUp: "triple" };
}

export function activateHeldPowerUp(
  current: RunState,
  now: number,
  tripleDuration: number,
): RunState {
  if (current.result !== "playing" || current.heldPowerUp !== "triple")
    return current;
  return {
    ...current,
    heldPowerUp: null,
    tripleUntil: Math.max(current.tripleUntil, now) + tripleDuration,
  };
}

export function clearMilestone(current: RunState): RunState {
  return current.milestone === null ? current : { ...current, milestone: null };
}

export function isTripleActive(current: RunState, now: number): boolean {
  return current.tripleUntil > now;
}

export function fillStage(hits: number, hitPoints: number): 0 | 1 | 2 | 3 {
  if (hits <= 0) return 0;
  if (hits >= hitPoints) return 3;
  return hits / hitPoints >= 0.66 ? 2 : 1;
}

export function earnedTitle(state: RunState, isBestScore: boolean): string {
  if (state.result === "victory" && state.lives >= STARTING_LIVES)
    return COPY.titles.flawless;
  if (state.result === "victory") return COPY.titles.champion;
  if (isBestScore && state.score > 0) return COPY.titles.personalBest;
  if (state.clogs >= 10) return COPY.titles.tenToilet;
  return COPY.titles.cadet;
}

export function mulberry32(seed: number): () => number {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let value = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    value = (value + Math.imul(value ^ (value >>> 7), 61 | value)) ^ value;
    return ((value ^ (value >>> 14)) >>> 0) / 4_294_967_296;
  };
}
